import * as React from 'react';
import { Subtract } from 'utility-types';

import { Filter } from '~commonInfra/utils/Filter';
import {IPredictiveModelDetailInfo, IPredictiveModelInfo} from '~src/predictiveModel/models/IPredictiveModelInfo';
import { PredictiveModels } from '~src/predictiveModel/resources/PredictiveModels';
import {IPredictiveModelActions} from '~src/predictiveModel/serverApi/types/IPredictiveModelActions';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import {SpacesSiteUrls} from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { RouterError } from '../../bootstrap/routerHelpers/RouterError';

interface InjectedPredictiveModelSpaceData {
  readonly predictiveModelAllowedActions: IPredictiveModelActions;
  readonly predictiveModelDetailInfo: IPredictiveModelDetailInfo;
  readonly predictiveModelInfo: IPredictiveModelInfo;
}

export interface InjectedPredictiveModelSpaceDataFetcherProps extends InjectedPredictiveModelSpaceData {
  readonly refreshData: () => void;
}

export interface PredictiveModelSpaceDataFetcherProps {
  readonly siteName: string;
  readonly predictiveModelId: string;
  readonly siteUrls: SpacesSiteUrls;
  readonly isRequestAccessEnabled: boolean;
}

export function withPredictiveModelSpaceDataFetcher<P extends InjectedPredictiveModelSpaceDataFetcherProps>(
  Component: React.ComponentType<P>
) {
  // TODO - achristian - TFSID:1059566 - 2020-02-03 - Redo this component with less side-effects.
  return function PredictiveModelSpaceDataFetcher(
    props: Subtract<P, InjectedPredictiveModelSpaceDataFetcherProps> & PredictiveModelSpaceDataFetcherProps
  ) {
    const [ error, setError ] = React.useState(false);
    const [ injectedData, setInjectedData ] = React.useState<InjectedPredictiveModelSpaceData>(undefined);
    const [ errorCode, setErrorCode ] = React.useState(null);

    let didCancel = false;

    const refreshData = () => {
      fetchPredictiveModelSpaceData(props.predictiveModelId)
        .then(injectedData => {
          !didCancel && setError(false);
          !didCancel && setInjectedData(injectedData);
        })
        .catch((e) => {
          !didCancel && setError(true);
          !didCancel && setErrorCode(e);
        });
    };

    React.useEffect(() => {
      refreshData();
      // This noncompliance existed before we enabled the lint rule. If you’re here, please consider fixing it!
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return () => didCancel = true;
    }, [ props.predictiveModelId, props.siteName ]);

    if (error) {
      if (props.isRequestAccessEnabled) {
        return (
          <RequestAccessError
            url={props.siteUrls.exploreUrl()}
            contentIdentifier={props.predictiveModelId}
            contentType={'predictiveModel'}
            errorCode={errorCode}
          />
        );
      }
      return <RouterError errorType={'predictiveModel'} urls={props.siteUrls}/>;
    } else if (!injectedData) {
      return null;
    }

    return <Component {...props as unknown as P} {...injectedData} refreshData={refreshData}/>;
  };
}

async function fetchPredictiveModelSpaceData(predictiveModelId: string): Promise<InjectedPredictiveModelSpaceData> {
  const predictiveModelAllowedActions: IPredictiveModelActions = await PredictiveModels.actions(predictiveModelId);
  const predictiveModelDetailInfo: IPredictiveModelDetailInfo = await PredictiveModels.getById(predictiveModelId);
  const predictiveModelInfo: IPredictiveModelInfo = await PredictiveModels.all().withFilters([Filter.fieldEq('id', predictiveModelId)]).getFirst();

  return {
    predictiveModelAllowedActions,
    predictiveModelDetailInfo,
    predictiveModelInfo,
  };
}
